import { router } from '@inertiajs/react'
import { useState } from 'react'
import { Smile, Meh, Frown } from 'lucide-react'
import type { ArticleRatingCounts } from '@/types'

interface ArticleRatingWidgetProps {
  articleId: number
  userRating: 1 | 2 | 3 | null
  ratingCounts: ArticleRatingCounts
  isAuthor: boolean
  isAdmin: boolean
}

// Rating values: 1 = sad, 2 = neutral, 3 = happy
const HAPPY = 3
const NEUTRAL = 2
const SAD = 1

export function ArticleRatingWidget({
  articleId,
  userRating,
  ratingCounts,
  isAuthor,
  isAdmin
}: ArticleRatingWidgetProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hoveredRating, setHoveredRating] = useState<number | null>(null)

  const handleRate = (value: number) => {
    if (isSubmitting || isAuthor) return

    if (userRating === value) {
      setIsSubmitting(true)
      router.delete(`/article_ratings/${articleId}`, {
        preserveScroll: true,
        onFinish: () => setIsSubmitting(false)
      })
    } else {
      setIsSubmitting(true)
      router.post('/article_ratings', {
        article_id: articleId,
        rating_value: value
      }, {
        preserveScroll: true,
        onFinish: () => setIsSubmitting(false)
      })
    }
  }

  const totalRatings = ratingCounts.happy + ratingCounts.neutral + ratingCounts.sad

  if (isAuthor) {
    if (!isAdmin || totalRatings === 0) return null

    return (
      <div className="flex items-center justify-center gap-3 py-4 text-sm text-gray-400">
        <span>{totalRatings} avis</span>
        <RatingMiniStats counts={ratingCounts} />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center gap-3 py-6">
      <p className="text-sm text-gray-500">
        Cet article vous a-t-il ete utile ?
      </p>

      <div className="flex items-center gap-1">
        <RatingIcon
          value={HAPPY}
          icon={Smile}
          isSelected={userRating === HAPPY}
          isHovered={hoveredRating === HAPPY}
          onClick={() => handleRate(HAPPY)}
          onMouseEnter={() => setHoveredRating(HAPPY)}
          onMouseLeave={() => setHoveredRating(null)}
          disabled={isSubmitting}
          color="green"
        />
        <RatingIcon
          value={NEUTRAL}
          icon={Meh}
          isSelected={userRating === NEUTRAL}
          isHovered={hoveredRating === NEUTRAL}
          onClick={() => handleRate(NEUTRAL)}
          onMouseEnter={() => setHoveredRating(NEUTRAL)}
          onMouseLeave={() => setHoveredRating(null)}
          disabled={isSubmitting}
          color="yellow"
        />
        <RatingIcon
          value={SAD}
          icon={Frown}
          isSelected={userRating === SAD}
          isHovered={hoveredRating === SAD}
          onClick={() => handleRate(SAD)}
          onMouseEnter={() => setHoveredRating(SAD)}
          onMouseLeave={() => setHoveredRating(null)}
          disabled={isSubmitting}
          color="red"
        />
      </div>

      {isAdmin && totalRatings > 0 && (
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>{totalRatings} avis</span>
          <RatingMiniStats counts={ratingCounts} />
        </div>
      )}

      {userRating && (
        <p className="text-xs text-gray-400">
          Merci pour votre retour
        </p>
      )}
    </div>
  )
}

interface RatingIconProps {
  value: number
  icon: typeof Smile
  isSelected: boolean
  isHovered: boolean
  onClick: () => void
  onMouseEnter: () => void
  onMouseLeave: () => void
  disabled: boolean
  color: 'green' | 'yellow' | 'red'
}

function RatingIcon({
  icon: Icon,
  isSelected,
  isHovered,
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled,
  color
}: RatingIconProps) {
  const colorClasses = {
    green: {
      default: 'text-gray-300',
      hover: 'text-green-400',
      selected: 'text-green-500'
    },
    yellow: {
      default: 'text-gray-300',
      hover: 'text-amber-400',
      selected: 'text-amber-500'
    },
    red: {
      default: 'text-gray-300',
      hover: 'text-red-400',
      selected: 'text-red-500'
    }
  }

  const classes = colorClasses[color]

  const getColorClass = () => {
    if (isSelected) return classes.selected
    if (isHovered) return classes.hover
    return classes.default
  }

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      className={`
        p-2 rounded-full transition-all duration-150
        ${getColorClass()}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-gray-50'}
        ${isSelected ? 'scale-110' : ''}
      `}
    >
      <Icon
        size={24}
        strokeWidth={isSelected ? 2.5 : 1.5}
      />
    </button>
  )
}

interface RatingMiniStatsProps {
  counts: ArticleRatingCounts
}

function RatingMiniStats({ counts }: RatingMiniStatsProps) {
  const total = counts.happy + counts.neutral + counts.sad
  if (total === 0) return null

  return (
    <div className="flex items-center gap-1">
      {counts.happy > 0 && (
        <span className="flex items-center gap-0.5 text-green-500">
          <Smile size={12} strokeWidth={2} />
          <span>{counts.happy}</span>
        </span>
      )}
      {counts.neutral > 0 && (
        <span className="flex items-center gap-0.5 text-amber-500">
          <Meh size={12} strokeWidth={2} />
          <span>{counts.neutral}</span>
        </span>
      )}
      {counts.sad > 0 && (
        <span className="flex items-center gap-0.5 text-red-500">
          <Frown size={12} strokeWidth={2} />
          <span>{counts.sad}</span>
        </span>
      )}
    </div>
  )
}
